import { GridLayout } from '@deity/falcon-ui'; // Still need falcon-ui, don't have gridTemplateColumns setting in portal-ui
import { mapRecord, mapRecordToArray } from '@deity-io/di-data-basics';
import { Box } from '@deity-io/portal-ui';
import type { FC, ReactElement } from 'react';
import { useState } from 'react';
import type { IContainerChildrenProps } from 'utils';
import type { IEditGridColumnsProps } from './EditGridColumn';
import EditGridRow from './EditGridRow';

interface IEditGridProps extends IContainerChildrenProps {
  locked?: boolean;
  columns: IEditGridColumnsProps;
  value: Array<Record<string, any>>;
  checked?: string | ((row: Record<string, any>) => boolean);
  error?: boolean | ((row: Record<string, any>) => boolean);
  onToggleChecked?: (index: number, checked: boolean) => void;
  onDeleteRow?: (index: number) => void;
  onChange?: (index: number, column: string, value: string) => void;
}

export const EditGrid: FC<IEditGridProps> = ({
  locked,
  columns,
  value,
  onToggleChecked,
  onDeleteRow,
  onChange,
  ...options
}) => {
  const [state, setState] = useState<{ currentKey?: number; edit?: string }>({});

  const toggleEdit = (index: number, type: string, editOn: boolean) => {
    if (editOn) {
      setState({ ...state, currentKey: index, edit: type });
    } else if (state.currentKey === index && state.edit === type) {
      setState({ ...state, currentKey: undefined, edit: undefined });
    }
  };

  const rowChecked = (row): boolean => {
    if (options.checked === undefined) {
      return false;
    }
    if (typeof options.checked === 'string') {
      // eslint-disable-next-line eqeqeq
      return row[options.checked] == true;
    }

    return options.checked(row);
  };

  const rowError = (row): boolean => {
    if (options.error === undefined) {
      return false;
    }
    if (typeof options.error === 'boolean') {
      return options.error;
    }

    return options.error(row);
  };

  const cells: Array<ReactElement> = [];

  // add Header
  cells.push(
    <Box key="_header_row" style={{ display: 'contents' }}>
      <Box key="_header_row_checkbox" cursor="arrow" />
      {mapRecordToArray(columns, (column, key) => (
        <Box key={`_header_row_${key}_field`} cursor="default">
          {column.label || key}
        </Box>
      ))}
      <Box />
    </Box>
  );

  let count = 0;
  // add values
  value?.forEach(row => {
    cells.push(
      <EditGridRow
        columns={columns}
        // eslint-disable-next-line react/no-array-index-key
        key={`_row_${count}`}
        rowIndex={count}
        row={row}
        onToggleEdit={onChange ? toggleEdit : undefined} // (index: number, type: string, editOn: boolean) => void;
        editColumn={state.currentKey === count ? state.edit : undefined}
        options={{ hasCheckBox: true }}
        checked={rowChecked(row)}
        error={rowError(row)}
        onToggleChecked={onToggleChecked}
        onChange={onChange} // (index: number, column: string, value: string) => void;
        onDelete={onDeleteRow}
      />
    );
    // eslint-disable-next-line no-plusplus
    count++;
  });

  // add new row placeholder
  if (!locked && onChange) {
    const newRow = mapRecord(columns, (column, key) => column.placeholder);
    cells.push(
      <EditGridRow
        columns={columns}
        // eslint-disable-next-line react/no-array-index-key
        key={`_row_${count}`}
        rowIndex={count}
        row={undefined}
        onToggleEdit={toggleEdit}
        editColumn={state.currentKey === count ? state.edit : undefined}
        options={{ hasCheckBox: true }}
        checked={rowChecked(newRow)}
        onToggleChecked={onToggleChecked}
        onChange={onChange}
      />
    );
  }
  const templateColumns = `1fr ${mapRecordToArray(columns, column => column.width || '1fr').join(' ')} 1fr`;

  return (
    <GridLayout className="request-explorer-grid" width="full" gridTemplateColumns={templateColumns}>
      {cells}
    </GridLayout>
  );
};
